import create from 'zustand';
import { setCache, getCache } from '../hook/useCache';
import { CacheEnum } from '../enum/cache';
import { MenuItem } from '@/pages/layout/components/sider/menuList';
import { ColorModeType } from 'src/config/type';
import { webSettings } from 'src/config/setting';
import { TabProps } from './type';

interface StoreState {
  token: string | undefined;
  localeMode: string;
  userCountry: string;
  menuFold: boolean;
  siderCollapsed: boolean;
  openRouterList: MenuItem[];
  openMenuBar: string[];
  selectMenuBar: string[];
  colorMode: ColorModeType;
  tabList: TabProps[];
  loginData: { id?: string; country?: string };
  setToken: (token: string) => void;
  setTabList: (tabs: TabProps[]) => void;
  setColorMode: (mode: ColorModeType) => void;
  setOpenMenuBar: (menu: string[]) => void;
  setSelectMenuBar: (menu: string[]) => void;
  setOpenRouter: (t: MenuItem[]) => void;
  setLocaleMode: (mode: string) => void;
  setCountryMode: (mode: string) => void;
  setLoginData: (data: { id?: string; country?: string }) => void;
  setSiderCollapsed: (collapsed: boolean) => void;
}

const useStore = create<StoreState>((set) => ({
  token:
    getCache({
      key: CacheEnum.token,
      storage: localStorage,
    }) || undefined,
  // 登录数据（用户ID和国家）
  loginData: {
    id:
      getCache({
        key: CacheEnum.userId,
        storage: localStorage,
      }) || undefined,
    country:
      getCache({
        key: CacheEnum.userCountry,
        storage: localStorage,
      }) || undefined,
  },
  setLoginData: (data: { id?: string; country?: string }) => {
    if (data.id) {
      setCache({
        key: CacheEnum.userId,
        value: data.id,
        storage: localStorage,
        timeout: 30 * 24 * 60 * 60, // 30天
      });
    }
    if (data.country) {
      setCache({
        key: CacheEnum.userCountry,
        value: data.country,
        storage: localStorage,
        timeout: 30 * 24 * 60 * 60, // 30天
      });
    }
    return set(() => ({ loginData: data }));
  },
  setToken: (token: string) => {
    setCache({
      key: CacheEnum.token,
      value: token,
      storage: localStorage,
      timeout: 30 * 24 * 60 * 60, // 这里设置为一个月（单位：秒）
    });
    return set(() => ({
      token,
    }));
  },
  // 菜单展开
  menuFold: false,
  // 侧边栏折叠状态
  siderCollapsed:
    getCache({
      key: CacheEnum.siderCollapsed,
      storage: localStorage,
    }) === true,
  // 语言
  localeMode: 'zh_CN',
  setLocaleMode: (mode: string) => set(() => ({ localeMode: mode })),
  // 国家
  userCountry: getCache({
    key: CacheEnum.userCountry,
    storage: localStorage,
  }),
  setCountryMode: (mode: string) => {
    setCache({
      key: CacheEnum.userCountry,
      value: mode,
      storage: localStorage,
    });
    return set(() => ({ userCountry: mode }));
  },
  // 面包屑数据
  openRouterList:
    getCache({
      key: CacheEnum.openRouteList,
      storage: sessionStorage,
    }) || [],
  setOpenRouter: (pathList: MenuItem[]) => {
    setCache({
      key: CacheEnum.openRouteList,
      value: pathList,
      storage: sessionStorage,
    });
    return set(() => ({
      openRouterList: pathList,
    }));
  },
  // tab标签栏数据
  tabList:
    getCache({
      key: CacheEnum.tabs,
      storage: sessionStorage,
    }) || webSettings.defaultTabs,
  setTabList: (tabs: TabProps[]) => {
    setCache({
      key: CacheEnum.tabs,
      value: tabs,
      storage: sessionStorage,
    });
    return set(() => ({
      tabList: tabs,
    }));
  },
  // 色彩模式
  colorMode:
    getCache({
      key: CacheEnum.colorMode,
      storage: localStorage,
    }) || webSettings.colorMode,
  setColorMode: (mode: ColorModeType) => {
    setCache({
      key: CacheEnum.colorMode,
      value: mode,
      storage: localStorage,
    });
    return set(() => ({ colorMode: mode }));
  },
  // 展开的菜单
  openMenuBar:
    getCache({ key: CacheEnum.openMenu, storage: sessionStorage }) || [],
  setOpenMenuBar: (menu: string[]) => {
    setCache({
      key: CacheEnum.openMenu,
      value: menu,
      storage: sessionStorage,
    });
    return set(() => ({ openMenuBar: menu }));
  },
  // 选中的菜单
  selectMenuBar:
    getCache({ key: CacheEnum.selectMenu, storage: sessionStorage }) || [],
  setSelectMenuBar: (menu: string[]) => {
    setCache({
      key: CacheEnum.selectMenu,
      value: menu,
      storage: sessionStorage,
    });
    return set(() => ({ selectMenuBar: menu }));
  },
  // 设置侧边栏折叠状态
  setSiderCollapsed: (collapsed: boolean) => {
    setCache({
      key: CacheEnum.siderCollapsed,
      value: collapsed,
      storage: localStorage,
    });
    return set(() => ({ siderCollapsed: collapsed }));
  },
}));

export default useStore;
